import { UserCompanyModel } from './../../shared/models/user-company-model';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserCredentialsModel } from '../models/user-credentials-model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../environments/environment';
import { CompanyModel } from 'src/app/shared/models/company-model';
import { TranslateService } from '@ngx-translate/core';
import { BetaWarningComponent } from 'src/app/shared/components/beta-warning/beta-warning.component';
import { MatDialog } from '@angular/material/dialog';
import { DistributorModel } from 'src/app/shared/models/distributor-model';
import { ToastrService } from 'ngx-toastr';
import { FeaturesModel } from 'src/app/shared/models/features-model';
import { isAdmin, isEndCompany, isViewer, roles } from 'src/constants/roles';
import { EventService } from 'src/app/panel/services/event.service';
import constants from 'src/app/shared/constants';

const DEFAULT_USER_ROLE = 'END_COMPANY';
const LOGIN_PATH = '/login';

// Todo lo que la sesión deja en localStorage. Estaba repetido clave por clave dentro de logout(),
// con 'companyId' después de la navegación, donde en producción ya no se ejecutaba.
const STORED_SESSION_KEYS = [
  'token',
  'refresh-token',
  'mean-token',
  'user-Commands',
  'user-Companies',
  'user-distributor',
  'selectedLanguage',
  'last-navigation-path',
  'beta-warning',
  'message-checks',
  'repository-config',
  'companyId',
  constants.companyIdChosenByAdmin,
];
const jsonLang: string[] = ['es', 'en', 'pt'];
interface TokenResponse {
  success: boolean;
  token: string;
  availableCommands: number[];
  companies: CompanyModel[];
  distributor: DistributorModel;
  config: RepoConfig;
  features?: FeaturesModel[];
}

interface RepoConfig {
  databusGuideURL: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private token: string;
  private readonly jwtHelper: JwtHelperService = new JwtHelperService();
  public user: UserCompanyModel;
  private currentTokenCheckID;
  public defaultLanguage: string = 'es';
  public selectedLanguage: string;
  public repositoryConfig: any;
  private expiredTokenToastShow = false;

  private readonly currentBetaWarningVersion: number = 1; // Increase on future BetaWarningComponent updates
  emptyCompany = new CompanyModel();

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
    private readonly dialog: MatDialog,
    private readonly toastrService: ToastrService,
    private readonly eventService: EventService,
  ) {
    this.selectedLanguage = this.managerLanguage();
  }

  private saveToken(
    token: string,
    userCommands: number[],
    userCompanies: CompanyModel[],
    userDistributor: DistributorModel,
  ): void {
    localStorage.setItem('mean-token', token);
    localStorage.setItem('user-Commands', JSON.stringify(userCommands));
    localStorage.setItem('user-Companies', JSON.stringify(userCompanies));
    localStorage.setItem('user-distributor', JSON.stringify(userDistributor));
    this.token = token;
    this.expiredTokenToastShow = false;
  }

  private saveLoginData(config): void {
    localStorage.setItem('repository-config', JSON.stringify(config));
  }

  // Método para definir el idioma inicial, en caso de no haber uno seleccionado por el usuario se
  // definirá el del navegador, en caso de no tener traducción del mismo, se configurará español por default.
  public selectInitialLang(): string {
    let initialLang: string;

    if (this.selectedLanguage) {
      initialLang = this.selectedLanguage;
    } else if (jsonLang.includes(navigator.language.split('-')[0])) {
      initialLang = navigator.language.split('-')[0];
    } else {
      initialLang = this.defaultLanguage;
    }

    return initialLang;
  }

  //Método para manejar los cambios de idioma según haya o no idioma guardado en sesión.
  public managerLanguage(): string {
    this.selectedLanguage = localStorage.getItem('selectedLanguage')
      ? localStorage.getItem('selectedLanguage')
      : this.selectInitialLang();

    return this.selectedLanguage;
  }

  //Método para guardar en sesión el idioma seleccionado por el usuario.
  public saveSelectedLanguage(language?: string): void {
    if (language) this.selectedLanguage = language;

    if (this.token != undefined) {
      localStorage.setItem('selectedLanguage', this.selectedLanguage);
    }
  }

  failSafeParseJson(jsonTextData: string) {
    try {
      return JSON.parse(jsonTextData);
    } catch (error) {
      return undefined;
    }
  }

  public getToken(): string {
    if (!this.token && localStorage.getItem('user-Companies')) {
      this.token = localStorage.getItem('mean-token');
      const userCommands = this.failSafeParseJson(localStorage.getItem('user-Commands'));
      const companies = this.failSafeParseJson(localStorage.getItem('user-Companies'));
      const distributor = this.failSafeParseJson(localStorage.getItem('user-distributor'));
      const config = this.failSafeParseJson(localStorage.getItem('repository-config'));
      const betaWarning = this.failSafeParseJson(localStorage.getItem('beta-warning'));
      const messageChecks = this.failSafeParseJson(localStorage.getItem('message-checks'));

      if (!this.jwtHelper.isTokenExpired(this.token)) {
        this.setUser({
          token: this.token,
          availableCommands: userCommands,
          companies,
          distributor,
          config,
          betaWarning,
          messagesChecks: messageChecks,
        });

        const path = localStorage.getItem('last-navigation-path') || '/maps';

        this.router.navigateByUrl(path);
      } else {
        this.token = undefined;
      }
    }

    if (this.token) this.checkTokenRenew();

    return this.token;
  }

  public checkTokenRenew() {
    const { exp } = this.jwtHelper.decodeToken(this.token);
    const expiresIn = exp ? exp * 1000 - new Date().getTime() : -1;

    if (expiresIn > 0 && expiresIn <= 10 * 60 * 1000) {
      // Si esta por vencer se intenta renovar
      this.checkAccessToken();
    } else if (!this.currentTokenCheckID) {
      this.currentTokenCheckID = setTimeout(
        () => {
          this.currentTokenCheckID = undefined;
          this.checkAccessToken();
        },
        expiresIn - 10 * 60 * 1000,
      );
    }
  }

  public cloudLogin(): Observable<any> {
    const cloudToken = localStorage.getItem('token');
    const request = this.http
      .get(`${environment.webappEndpoint}/auth/token`, {
        headers: { Authorization: `Bearer ${cloudToken}` },
      })
      .pipe(
        map((data: TokenResponse) => {
          if (data.success && data.token) {
            localStorage.removeItem(constants.companyIdChosenByAdmin);
            this.setUser(data);
            this.saveToken(data.token, data.availableCommands, data.companies, data.distributor);
            this.saveLoginData(data.config);
            this.saveSelectedLanguage();
            this.openBetaWarningModal();
          }
          return data;
        }),
      );

    return request;
  }

  public login(user: UserCredentialsModel): Observable<any> {
    const request = this.http
      .post(`${environment.webappEndpoint}/auth/login`, { username: user.email, password: user.pass })
      .pipe(
        map((data: TokenResponse) => {
          if (data.success && data.token) {
            localStorage.removeItem(constants.companyIdChosenByAdmin);
            this.setUser(data);
            this.saveToken(data.token, data.availableCommands, data.companies, data.distributor);
            this.saveLoginData(data.config);
            this.saveSelectedLanguage();
            this.openBetaWarningModal();
          }
          return data;
        }),
      );

    return request;
  }

  public checkAccessToken(props = {}) {
    this.http
      .post(`${environment.webappEndpoint}/auth/checkAccessToken`, props, {
        headers: { Authorization: `Bearer ${this.token}` },
      })
      .subscribe({
        next: (res: any) => {
          if (res.success && res.token) {
            this.currentTokenCheckID = undefined;
            const companies = res.companies ?? this.user.companies;
            const distributor = res.distributor ?? this.user.distributor;
            const availableCommands = this.user.availableCommands;
            const features = res.features ?? this.user.features;
            this.setUser({ token: res.token, availableCommands, companies, distributor, features });
            this.saveToken(res.token, availableCommands, companies, distributor);
            this.checkTokenRenew();
          }
          // Si falla control de accessToken se fuerza el deslogueo
          if (!res.success) this.logout();
          return res;
        },
        error: (error) => {
          if (error.error.status == 'UNAUTHORIZED' && error.error.code == 401) {
            if (!this.expiredTokenToastShow)
              this.toastrService.info(
                this.translateService.instant('Shared.SessionExpired'),
                this.translateService.instant('Shared.Attention'),
                {
                  timeOut: 5_000,
                  closeButton: true,
                  easing: 'ease',
                  easeTime: 300,
                  tapToDismiss: true,
                  newestOnTop: true,
                },
              );
            this.expiredTokenToastShow = true;
            this.logout();
          }
        },
      });
  }

  /**
   * El logout era sólo local: el socket seguía abierto del lado del servidor, unido a las salas y
   * recibiendo todo lo que se emite a ellas. El servidor no puede deducir de quién es cada socket
   * (el token del handshake es de servicio), así que hay que pedirle explícitamente que cierre los
   * de este usuario. Se espera la respuesta porque en producción el logout navega con
   * window.location.href y cancelaría el pedido en vuelo.
   */
  public async closeServerSession(): Promise<void> {
    if (!this.token) return;

    try {
      await firstValueFrom(
        this.http.post(
          `${environment.webappEndpoint}/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${this.token}` } }
        )
      );
    } catch (error) {
      // Cerrar sesión no puede quedar bloqueado porque el backend no responda.
      Sentry.captureException(error);
    }
  }

  public logout(): void {
    this.token = undefined;
    this.user = undefined;
    STORED_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
    this.redirectToLogin();
  }

  /**
   * El logout tiene que dejar la aplicación como recién abierta. Una navegación de Angular conserva
   * los servicios singleton del usuario anterior —el socket y su watchdog, las listas ya cargadas,
   * el mapa— y esa mezcla de estado viejo con sesión nueva es la que terminaba obligando a recargar
   * a mano. Por eso siempre se sale con una carga completa de la página, no sólo en producción.
   */
  private redirectToLogin(): void {
    window.location.href = LOGIN_PATH;
  }

  public getUserDetails(): UserCompanyModel {
    const token = this.getToken();
    if (token) {
      return this.jwtHelper.decodeToken(token);
    } else {
      return null;
    }
  }

  public isLoggedInCloud(): boolean {
    const cloudToken = localStorage.getItem('token');
    if (cloudToken) {
      return !this.jwtHelper.isTokenExpired(cloudToken);
    } else {
      return false;
    }
  }

  public isLoggedIn(): boolean {
    const token = this.getToken();
    if (token) {
      return !this.jwtHelper.isTokenExpired(token);
    } else {
      return false;
    }
  }

  private setUser({
    token,
    availableCommands,
    companies,
    distributor,
    config,
    betaWarning,
    messagesChecks,
    features,
  }: {
    token: string;
    availableCommands: number[];
    companies: CompanyModel[];
    distributor: DistributorModel;
    config?: RepoConfig;
    betaWarning?: number;
    messagesChecks?: any;
    features?: FeaturesModel[];
  }) {
    const data = this.jwtHelper.decodeToken(token);
    const userCompanies = distributor?.companies || companies;
    this.user = {
      _id: data._id,
      email: data.email,
      name: data.name,
      lastName: data.lastName,
      phone: data.phone,
      language: data.language,
      enableTechSupport: data.enableTechSupport,
      unmonitoredCompany: data.unmonitoredCompany,
      availableCommands: availableCommands ?? [],
      betaWarning: betaWarning ?? data.betaWarning ?? 0,
      messagesChecks: messagesChecks ?? data.messagesChecks ?? {},
      companies: userCompanies,
      role: data.role ?? DEFAULT_USER_ROLE,
      distributor: distributor,
      features: features ?? data.features,
    };

    if (this.user.companies) {
      this.user.companies.sort((a: CompanyModel, b: CompanyModel) => a.name.localeCompare(b.name));
    }

    Sentry.setUser({ id: data._id, email: data.email });

    this.saveSelectedLanguage(data.language);
    if (config) this.repositoryConfig = config;

    setTimeout(() => {
      this.eventService.adminChooseCompanyEvent.emit({ openPopup: true });
    }, 100);
  }

  // CI-2955: o usuario global (ADMIN) nao e validado por feature flag — tem acesso a todas
  // as features. O backend deixa de calcular features para ele, porque a lista de empresas
  // de cada flag estourava o limite de header do nginx no token do usuario global.
  isFeatureFlagEnabled = (featureName: string, companyId: string): boolean => {
    if (this.hasRoleAdmin()) return true;

    const featureFlag = this.user?.features?.find((f) => f.name === featureName);

    return !!featureFlag?.companies?.includes(companyId);
  };

  addCompany(company) {
    this.user.companies.push(company);
    this.user.companies.sort((a: CompanyModel, b: CompanyModel) => a.name.localeCompare(b.name));
  }

  getCompanyName = (companyID: string) => {
    if (!companyID) return '';

    const companyFound = this.user.companies.find((c) => c._id === companyID);

    if (!companyFound) {
      this.snackBar.open(
        this.translateService.instant('Shared.NotFound'),
        this.translateService.instant('Shared.Accept'),
        { duration: 5000 },
      );
    }

    return companyFound?.name ? companyFound.name : '';
  };

  getPrimaryCompany = () => {
    if (this.hasRoleAdmin()) {
      const companyIdChosenByAdmin = localStorage.getItem(constants.companyIdChosenByAdmin);
      const company = this.user.companies.find((c) => c._id === companyIdChosenByAdmin);
      if (company) return company;
    }

    if (this.user?.companies?.length > 0) {
      return this.user.companies[0];
    } else if (this.user?.company) {
      return this.user.company;
    }

    return this.emptyCompany;
  };

  getCompanies = () => {
    if (this.hasRoleAdmin()) {
      const companyIdChosenByAdmin = localStorage.getItem(constants.companyIdChosenByAdmin);
      const company = this.user.companies.find((c) => c._id === companyIdChosenByAdmin);
      if (company) return [company];
    }

    return this.user.companies;
  };

  hasRoleAdmin = () => {
    return this.user?.role === roles.ADMIN;
  };

  hasRoleDistributor = () => {
    return this.user?.role === roles.ADMIN_DISTRIBUTOR;
  };

  hasAdminCompany = () => {
    return this.user?.role === roles.ADMIN_COMPANY;
  };

  hasRoleEndCompany = () => {
    return isEndCompany(this.user?.role);
  };

  hasRolesAdminOrDistributor = () => {
    return this.user?.role === roles.ADMIN || this.user?.role === roles.ADMIN_DISTRIBUTOR;
  };

  canManageKaironVoice = (companyId: string): boolean => {
    if (!this.hasRoleAdmin() && !this.hasAdminCompany() && !this.hasRoleDistributor()) return false;
    return this.isFeatureFlagEnabled(constants.featureFlags.iaKairon, companyId);
  };

  isAdmin = () => {
    // Legacy logic kept for compatibility
    return this.getCompanies().length > 1;
  };

  hasOneCompany = () => {
    return this.getCompanies().length === 1;
  };

  hasMultipleCompanies = () => {
    return this.getCompanies().length > 1;
  };

  shouldBlockViewer = () => {
    return isViewer(this.user?.role);
  };

  isReadOnlyMode = () => {
    return isViewer(this.user?.role) || isAdmin(this.user?.role);
  };


  public updateUserBetaVersion(version: number): Observable<any> {
    const request = this.http
      .post(
        `${environment.webappEndpoint}/userCompany/betaVersion`,
        { version },
        { headers: { Authorization: `Bearer ${this.token}` } },
      )
      .pipe();

    return request;
  }

  openBetaWarningModal(): void {
    if (this.hasRoleAdmin()) {
      return;
    }

    if (this.user && (!this.user?.betaWarning || this.user?.betaWarning < this.currentBetaWarningVersion)) {
      const dialogRef = this.dialog.open(BetaWarningComponent, {
        maxWidth: '570px',
        autoFocus: false,
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result?.status === 'gotIt') {
          this.user.betaWarning = this.currentBetaWarningVersion;
          this.updateUserBetaVersion(this.currentBetaWarningVersion).subscribe((response) => {
            if (response.success) localStorage.setItem('beta-warning', JSON.stringify(this.currentBetaWarningVersion));
          });
        }
      });
    }
  }

  updateMessageCheck(field): void {
    this.user.messagesChecks[field] = true;
    this.updateUserMessageCheck(field).subscribe((response) => {
      if (response.success) localStorage.setItem('message-checks', JSON.stringify(this.user.messagesChecks));
    });
  }

  public updateUserMessageCheck(field: string): Observable<any> {
    const request = this.http
      .post(
        `${environment.webappEndpoint}/userCompany/messageChecks`,
        { field },
        { headers: { Authorization: `Bearer ${this.token}` } },
      )
      .pipe();

    return request;
  }
}
